/**
 * Which NODE a write addresses — the pure half of STD-029 R-11.
 *
 * Every file-management write is id-addressed (B-3), so the browser has to hold
 * an address for both ends of an operation. B-3 shipped this module with **two**
 * ways the answer could be no, described as equally temporary. Only one of them
 * was:
 *
 *  - the **destination** could have no id — an ancestor breadcrumb, whose id the
 *    flat browse projection did not carry. **That case is gone** (B-4): the
 *    children listing carries the ancestor chain, so every crumb is a real node
 *    and `parentIdFor` is total. Along with it goes `isAddressableDestination`
 *    and `crumbRef`, and with them D-4c — dragging onto a non-root breadcrumb
 *    works again.
 *  - the **entry** can have no `object_id` — an R-9 flight with no registry row.
 *    **That case is permanent** and the rule for it is unchanged: hide the
 *    id-keyed affordance, never offer one that must then be refused.
 *
 * The rules live here rather than inside the components because this app ships
 * no jsdom/RTL: a rule left in a hook has no test tier that can hold it, and
 * these are exactly the rules whose failure mode is silent.
 */
import type { FileItem, FolderRef } from './types';

/**
 * The `parent_id` to send for a destination folder.
 *
 * Total, since B-4. `null` MEANS the caller's own organization root — the
 * payload `POST /api/file-objects/folders` and `PATCH /api/file-objects/{id}`
 * take, which the server resolves from the JWT and can therefore never point at
 * anyone else's root.
 *
 * It used to have a third answer, `undefined` for "a folder we can name but not
 * address", kept deliberately distinct from `null` because folding them would
 * move a file to the organization root instead of refusing. Both are `string |
 * null` to a type-checker, so the distinction could only ever be held by hand.
 * The third answer is not folded here — it stopped existing.
 */
export function parentIdFor(folder: FolderRef): string | null {
  return folder.id;
}

/**
 * The entries a write may act on: exactly those carrying an `object_id`.
 *
 * Verbatim the doctrine R-13 states for sharing — "entries without `object_id`
 * are dropped from targets, not sent" — applied to move for the same reason. An
 * entry with no row cannot be addressed, and sending it would earn a 404 that
 * says nothing useful; dropping it lets the caller report it as "these did not
 * move" alongside anything that genuinely failed.
 *
 * **This survives B-4 and is not a countdown.** The unregistered arm is a
 * supported end state (R-9): `register_uploaded_file` fails soft, so a CSV
 * upload whose registry write did not take leaves a flight with no row, and that
 * file is listed — visible and inert — rather than hidden.
 */
export function writableEntries(entries: readonly FileItem[]): FileItem[] {
  return entries.filter((entry) => !!entry.object_id);
}

/**
 * Whether one entry can be renamed, moved or deleted through the id surface.
 *
 * The predicate the context menu gates on. R-9: an entry without a row degrades
 * by *hiding* id-keyed affordances, never by offering one that fails.
 */
export function isWritableEntry(entry: Pick<FileItem, 'object_id'>): boolean {
  return !!entry.object_id;
}
